import React from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Apple, Dumbbell, Scale } from 'lucide-react';
import { Badge, Card, Empty, Page, PageHead, Stat } from '../components/ui';
import {
  DIET_PLANS, MACROS, WORKOUT_PLANS, bmiBand, bodyMetrics, longDate, n0, n1, shortDate, titleise,
} from '../lib/format';

const axis = { stroke: 'var(--text-faint)', fontSize: 11, tickLine: false, axisLine: false };
const tip = {
  background: 'var(--surface-2)', border: '1px solid var(--border-strong)',
  borderRadius: 10, fontSize: 12, color: 'var(--text)',
};

export default function Dashboard({ user, foodLog, exerciseLog, weightLog, stats, plan, onGo }) {
  const totals = foodLog.reduce(
    (acc, f) => ({
      calories: acc.calories + (+f.calories || 0),
      protein: acc.protein + (+f.protein || 0),
      carbs: acc.carbs + (+f.carbs || 0),
      fats: acc.fats + (+f.fats || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fats: 0 }
  );

  const diet = DIET_PLANS[plan?.diet_plan] || DIET_PLANS.balanced;
  const workout = WORKOUT_PLANS[plan?.workout_plan] || WORKOUT_PLANS['beginner-full-body'];
  const target = { calories: diet.calories, protein: diet.protein, carbs: diet.carbs, fats: diet.fats };

  const latest = weightLog[weightLog.length - 1];
  const first = weightLog[0];
  const { bmi } = bodyMetrics(user?.profile, latest);
  const band = bmiBand(bmi);

  const change = latest && first ? latest.weight - first.weight : null;
  const toGo = user?.profile?.target_weight && latest
    ? latest.weight - user.profile.target_weight
    : null;

  const weightSeries = weightLog.map((w) => ({
    date: shortDate(w.logged_at),
    weight: w.weight,
    bodyFat: w.body_fat,
  }));

  const volume = exerciseLog.reduce(
    (sum, e) => sum + (+e.sets || 0) * (+e.reps || 0) * (+e.weight || 0), 0
  );

  return (
    <Page>
      <PageHead title={`Good to see you, ${(user?.full_name || user?.username || '').split(' ')[0]}`}>
        {longDate(new Date().toISOString())}
      </PageHead>

      <div className="grid grid-4">
        <Stat
          label="Today's calories"
          value={n0(totals.calories)}
          note={`of ${n0(target.calories)} kcal target`}
          tone={totals.calories > target.calories * 1.1 ? 'warn' : undefined}
        />
        <Stat
          label="Current weight"
          value={latest ? n1(latest.weight) : '–'}
          unit="kg"
          note={change != null ? `${change > 0 ? '+' : ''}${n1(change)} kg since you started` : 'No entries yet'}
          tone={change != null && change < 0 ? 'ok' : undefined}
        />
        <Stat
          label="BMI"
          value={bmi ? n1(bmi) : '–'}
          note={band.label}
          tone={band.tone === 'ok' ? 'ok' : band.tone === 'muted' ? undefined : band.tone}
        />
        <Stat
          label="Day streak"
          value={stats?.current_streak ?? 0}
          note={`${stats?.days_active ?? 0} days active in total`}
        />
      </div>

      <div className="grid grid-2">
        <Card
          title="Today's nutrition"
          sub={diet.name}
          actions={<Badge tone="muted">{foodLog.length} logged</Badge>}
        >
          {foodLog.length === 0 ? (
            <Empty icon={Apple} title="Nothing logged today">
              Add your first meal from the Food Log.
            </Empty>
          ) : (
            <div className="stack-tight">
              {MACROS.map((m) => {
                const value = totals[m.key];
                const max = target[m.key];
                const pct = max > 0 ? (value / max) * 100 : 0;
                return (
                  <div key={m.key}>
                    <div className="spread" style={{ marginBottom: 5 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 500 }}>{m.label}</span>
                      <span className="num" style={{ fontSize: 12.5 }}>
                        {n0(value)}<span className="faint"> / {n0(max)} {m.unit}</span>
                      </span>
                    </div>
                    <div className="bar">
                      <span style={{
                        width: `${Math.min(100, pct)}%`,
                        background: pct > 110 ? 'var(--warn)' : m.color,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card
          title="Today's training"
          sub={workout.name}
          actions={<Badge tone="muted">{exerciseLog.length} logged</Badge>}
        >
          {exerciseLog.length === 0 ? (
            <Empty icon={Dumbbell} title="No session logged today">
              Rest day, or one still to come.
            </Empty>
          ) : (
            <div className="stack-tight">
              {exerciseLog.slice(0, 5).map((e) => (
                <div className="spread" key={e.id}>
                  <div className="row-tight">
                    <Badge tone="muted">{titleise(e.muscle_group)}</Badge>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{e.name}</span>
                  </div>
                  <span className="num faint" style={{ fontSize: 12 }}>
                    {e.sets && e.reps ? `${e.sets}×${e.reps}` : ''}
                    {e.weight ? ` · ${n0(e.weight)}kg` : ''}
                    {e.duration ? `${e.duration} min` : ''}
                  </span>
                </div>
              ))}
              {volume > 0 && (
                <div className="spread" style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                  <span className="label">Total volume</span>
                  <span className="num" style={{ fontWeight: 600 }}>{n0(volume)} kg</span>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Weight trend"
        sub={weightLog.length ? `${weightLog.length} entries` : undefined}
        actions={
          toGo != null && (
            <Badge tone={Math.abs(toGo) < 0.5 ? 'ok' : 'accent'}>
              {Math.abs(toGo) < 0.5 ? 'At target' : `${n1(Math.abs(toGo))} kg to ${toGo > 0 ? 'lose' : 'gain'}`}
            </Badge>
          )
        }
      >
        {weightLog.length < 2 ? (
          <Empty icon={Scale} title="Not enough entries to chart">
            Log your weight a few times and the trend appears here.
          </Empty>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weightSeries} margin={{ top: 6, right: 12, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="wfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={30} />
                <YAxis {...axis} domain={['dataMin - 1', 'dataMax + 1']} width={42}
                  tickFormatter={(v) => v.toFixed(0)} />
                <Tooltip contentStyle={tip} formatter={(v, k) => [
                  k === 'weight' ? `${n1(v)} kg` : `${n1(v)} %`,
                  k === 'weight' ? 'Weight' : 'Body fat',
                ]} />
                <Area type="monotone" dataKey="weight" stroke="var(--accent)" strokeWidth={2}
                  fill="url(#wfill)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid grid-3">
        <Card title="Logging activity">
          <div className="stack-tight">
            {[
              { k: 'Meals', v: stats?.total_food_logs, icon: Apple },
              { k: 'Sessions', v: stats?.total_exercise_logs, icon: Dumbbell },
              { k: 'Weigh-ins', v: stats?.total_weight_logs, icon: Scale },
            ].map(({ k, v, icon: Icon }) => (
              <div className="spread" key={k}>
                <span className="row-tight dim" style={{ fontSize: 13 }}>
                  <Icon size={14} /> {k}
                </span>
                <span className="num" style={{ fontWeight: 600 }}>{n0(v ?? 0)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Your diet plan" sub={diet.description}>
          <div className="stack-tight">
            {diet.meals.slice(0, 3).map((m) => (
              <div key={m.meal}>
                <div className="label">{m.meal}</div>
                <div className="dim" style={{ fontSize: 12.5 }}>{m.items}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Your training plan" sub={workout.description}>
          <div className="stack-tight">
            {workout.schedule.slice(0, 3).map((d) => (
              <div className="spread" key={d.day}>
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>{d.day}</span>
                <span className="faint" style={{ fontSize: 12 }}>{d.exercises.length} lifts</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Page>
  );
}
